import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { InsertOneResult, UpdateResult } from 'mongodb';

export enum ContractSupplementDataType {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  DATE = 'date',
  JSON = 'json',
  NULL = 'null',
  NEW_FIELD = 'new_field',
}

/**
 * Describes the inner section of a ContractSupplement. A ContractSupplement can have three seperate sections which can change independently from one another.
 * As we just save changes with paths, we rely on the the different time intervalls to determine which paths are active.
 */
export type ContractSupplementInnerSection = 'general' | 'ag1' | 'ag2';

/**
 * @entity ContractSupplement
 *
 * Defines a change on a subentity
 */
@Entity()
export class ContractSupplementChange extends AdornisEntity {
  static override _class = 'ContractSupplementChange';

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() })
  _id!: string;

  /**
   * The path to the field which the ContractSupplement changes
   */
  @Field(() => String)
  path!: string;

  /**
   * The type of the field which the ContractSupplement changes. Is used to correctly parse the stringified value of the change
   */
  @Field(() => String)
  type!: ContractSupplementDataType;

  /**
   * The actual value which is filled in.
   */
  @Field(() => String)
  value!: string;
}

/**
 * Apply/display order of supplements: nachtragsNummer first, then subNachtragsnummer with a missing sub
 * counting as 0. Legacy data contains duplicate numbers - exact ties deliberately compare equal and keep
 * their insertion order (Array.sort is stable).
 */
export const compareSupplements = (
  a: { nachtragsNummer: number; subNachtragsnummer?: Maybe<number> },
  b: { nachtragsNummer: number; subNachtragsnummer?: Maybe<number> },
) =>
  a.nachtragsNummer === b.nachtragsNummer
    ? (a.subNachtragsnummer ?? 0) - (b.subNachtragsnummer ?? 0)
    : a.nachtragsNummer - b.nachtragsNummer;

@Entity()
export class ContractSupplement extends MongoEntity {
  static override _class = 'ContractSupplement';
  static override _collectionName = 'contract-supplements';

  @Field(() => Date, { default: v => v ?? new Date() })
  createdAt!: Date;

  @validate(nonOptional())
  @Field(() => ID)
  vertragID!: string;

  /** gets auto created on creation*/
  @validate(nonOptional())
  @Field(() => Int)
  nachtragsNummer!: number;

  @Field(() => Int)
  subNachtragsnummer: Maybe<number>;

  /**
   * We have three intervalls to determine which path gets applied in which timerange
   */
  @Field(() => Date)
  startDateGeneral: Maybe<Date>;

  @Field(() => Date)
  endDateGeneral: Maybe<Date>;

  @Field(() => Date)
  startDateAG1: Maybe<Date>;

  @Field(() => Date)
  endDateAG1: Maybe<Date>;

  @Field(() => Date)
  startDateAG2: Maybe<Date>;

  @Field(() => Date)
  endDateAG2: Maybe<Date>;

  /**
   * If the contract is manually ended per "gekuendigtZum" this field is set to said value. So every supplement no longer applies after this point in time.
   * With this we can keep the enddate of the supplement and still forcefully end it. If the contract is opend once more, this field is reset.
   */
  @Field(() => Date)
  automaticallyAssignedEndDateOfContract: Maybe<Date>;

  /**
   * At which point in time did the employee sign the new Vertrag with the Nachtrag. A non signed Nachtrag is not active until it is signed. For Drafting this field is iognored, as the internal personal does not really care if the employee has signed the nachtrag
   */
  @Field(() => Date)
  signedAt: Maybe<Date>;

  /**
   * Needed for legal reasons. The paragraph which the ContractSupplement is based on.
   */
  @Field(() => String)
  paragraph: Maybe<string>;

  /**
   * The person which creates the supplement should tell here why it was created.
   */
  @Field(() => String)
  justification: Maybe<string>;

  /**
   * The changes the nachtrag makes
   */
  @Field(() => [ContractSupplementChange])
  changes: Maybe<ContractSupplementChange[]>;

  static override async afterCreate(entity: ContractSupplement, result: InsertOneResult): Promise<void> {
    await super.afterCreate(entity, result);
    try {
      // no idea why this triggery a circular import
      if (baseConfig.get('DEPLOYMENT') !== 'testing') {
        const { sendContractSupplementCreationNotification } =
          await import('../server/sendContractSupplementCreationNotification.js');
        await sendContractSupplementCreationNotification(entity);
      }
    } catch (e) {
      logger.error(e, 'Fehler beim Versuch die ContractSupplementCreationMail zu senden. (afterCreate)');
    }
    await supplementChanged(entity.vertragID);
  }

  static override async afterUpdate(
    oldEntity: PartialEntityData<ContractSupplement>,
    newEntity: ContractSupplement,
    result: UpdateResult,
  ): Promise<void> {
    await super.afterUpdate(oldEntity, newEntity, result);
    await supplementChanged(newEntity.vertragID ?? oldEntity.vertragID);
  }

  static override async afterRemove(id: string, entityData: PartialEntityData<ContractSupplement>): Promise<void> {
    await super.afterRemove(id, entityData);
    await supplementChanged(entityData?.vertragID);
  }
}

/** A supplement changes its contract: the person behind it is looked up and their bookings and roles follow. */
async function supplementChanged(contractID: Maybe<string>): Promise<void> {
  if (!contractID) return;
  const { getRawCollection } = await import('@adornis/baseql/server/collections.js');
  const { Contract, contractChanged } = await import('./Contract.js');
  const contract = await (
    await getRawCollection<{ _id: string; userID: Maybe<string> }>(Contract._collectionName)
  ).findOne({ _id: contractID }, { projection: { userID: 1 } });
  await contractChanged(contract?.userID);
}
